import { api } from "../../api/index.ts";
import { useCallback } from "react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { extractApiError } from "../../api/errors.ts";
import { useShowToast } from "../../contexts/ToastContext.tsx";
import { ProductEntry } from "../../types/shared.ts";
import {
  productsCreateToExternal,
  productsUpdateToExternal,
} from "../../pages/products/converts.ts";

export const useSetProductData = () => {
  const showToast = useShowToast();

  const [triggerCreate] =
    api.endpoints.createProductApiProductsPost.useMutation();
  const [triggerUpdate] =
    api.endpoints.updateProductApiProductsProductIdPut.useMutation();
  const [triggerDelete] =
    api.endpoints.deleteProductApiProductsProductIdDelete.useMutation();

  const setData = useCallback(
    async (updatedRows: Partial<ProductEntry>[]) => {
      return await Promise.all(
        updatedRows.map((row) => {
          if (row.id) {
            const update = productsUpdateToExternal(row);
            if (Object.entries(update).length !== 0) {
              return triggerUpdate({
                productId: row.id,
                productUpdate: update,
              });
            } else {
              return undefined;
            }
          } else {
            return triggerCreate({
              productCreate: productsCreateToExternal(row as ProductEntry),
            });
          }
        }),
      )
        .then((results) => {
          console.info("Update results ", results);
          results.forEach((result) => {
            if (result && "error" in result) {
              showToast({
                severity: "error",
                summary: "Błąd",
                detail: extractApiError(
                  result.error as FetchBaseQueryError | SerializedError,
                ),
              });
            }
          });
          return results;
        })
        .catch((err) => {
          console.error(err);
          return null;
        });
    },
    [triggerCreate, triggerUpdate, showToast],
  );

  const deleteData = useCallback(
    async (productId: number) => {
      try {
        const result = await triggerDelete({ productId });
        console.info("Deleted product", productId, result);
        return result;
      } catch (error) {
        console.error("Delete error", error);
        throw error;
      }
    },
    [triggerDelete],
  );

  return { setData, deleteData };
};
